/* eslint-disable react/require-default-props */
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import { useIntl } from "../../../../../_metronic/i18n/customUseIntl";
import { ctaClick, formStart } from "../../../analytics/adobeDataLayer";
import dsmTheme from "../../../../modules/MUITheme";
import { useNotifications } from "../../../hooks/notifications/useNotifications";
import { useNotificationContext } from "../../../context/NotificationContext";
import { notificationAdminPageStyles } from "../../../../ThemeProvider";
import type {
  AppNotification,
  NotificationCategory,
} from "../../../models/Notification/notificationTypes";
import NotificationForm from "./NotificationForm";
import NotificationTable from "./NotificationTable";

type NotificationAdminPageProps = {
  category: NotificationCategory;
  onSaved?: () => void;
};

type ActiveView =
  | { mode: "list" }
  | { mode: "new" }
  | { mode: "edit"; notification: AppNotification };

const NotificationAdminPage = ({
  category,
  onSaved,
}: NotificationAdminPageProps) => {
  const intl = useIntl();
  const classes = notificationAdminPageStyles();

  const { data, isLoading, error, reload } = useNotifications({
    mode: "admin",
    category,
  });

  const { reload: reloadUserNotifications } = useNotificationContext();

  const [activeView, setActiveView] = useState<ActiveView>({ mode: "list" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleNewClick = () => {
    ctaClick(
      window.location.href,
      "button",
      "New Notification",
      "Admin",
      category,
    );
    setSelectedId(null);
    setActiveView({ mode: "new" });
    formStart("Add Notification", "new");
  };

  const handleRowClick = (notification: AppNotification) => {
    ctaClick(
      window.location.href,
      "link",
      "Edit Notification",
      "Admin",
      category,
    );
    setSelectedId(notification.id);
    setActiveView({ mode: "edit", notification });
    formStart("Edit Notification", "edit");
  };

  const handleFormClose = (saved?: AppNotification) => {
    if (saved) {
      reload();
      reloadUserNotifications();
      setSelectedId(saved.id);
      onSaved?.();
    }
    setActiveView({ mode: "list" });
  };

  if (activeView.mode === "new" || activeView.mode === "edit") {
    return (
      <ThemeProvider theme={dsmTheme}>
        <NotificationForm
          category={category}
          notification={
            activeView.mode === "edit" ? activeView.notification : undefined
          }
          onClose={handleFormClose}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={dsmTheme}>
      <Card className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h5" color="secondary" component="div">
            {intl.formatMessage({ id: "NOTIFICATIONS" })}
          </Typography>
          <Button variant="outlined" onClick={handleNewClick}>
            {intl.formatMessage({ id: "NOTIFICATIONS.NEW_NOTIFICATION" })}
          </Button>
        </Box>

        <NotificationTable
          category={category}
          data={data}
          isLoading={isLoading}
          error={error}
          selectedId={selectedId}
          onRowClick={handleRowClick}
        />
      </Card>
    </ThemeProvider>
  );
};

export default NotificationAdminPage;
